import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_INTEGRATIONS_MARKETPLACE_CARD } from '../../../webcomponents.constants';
import { FontSizes, FontWeights } from '../../../types/common/types';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../ui/gzButton/types';
import { translateFn } from '../../../utils/translator.helper';
import {
  IntegrationsMarketplaceCardActionButton,
  IntegrationsMarketplaceCardData,
  IntegrationsMarketplaceCardEvents,
} from '../types';
import { classMap } from 'lit/directives/class-map.js';
import { INTEGRATION_TYPES, LICENSE_STATUSES_INSTALLED } from '../../common/constants';
import { dateFormatter } from '@nirvana/shared/time';
import { Dependencies } from '../../integrationsMarketplaceAppContainer/types/dependencies';

@customElement(WEB_COMPONENT_GZ_INTEGRATIONS_MARKETPLACE_CARD)
export class GzIntegrationsMarketplaceCardWebComponent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  data: IntegrationsMarketplaceCardData;

  static get styles() {
    return styles;
  }

  @property({ type: Object })
  dependencies!: Dependencies;

  render(): TemplateResult {
    const wrapperClasses = classMap({
      'card-wrapper': true,
      readonly: this.data.readonly ?? false,
      'hover-editable': this.data.hoverEditable ?? false,
    });
    return html`
      <div
        class="${wrapperClasses}"
        @click="${e => {
          if (!(e.target as HTMLElement).classList.contains('link-button')) {
            this.handleAction(e, IntegrationsMarketplaceCardEvents.CARD_CLICK);
          }
        }}"
      >
        <gz-tooltip isEllipsis .tooltipText="${this.data.name}" class="centered d-block card-title">
          <gz-title fontSize="${FontSizes.HEADING_5}" fontWeight="${FontWeights.MEDIUM}" class="d-inline">
            ${this.data.name}
          </gz-title>
        </gz-tooltip>

        <div class="image-section">
          <div>
            <gz-tooltip isEllipsis .tooltipText="${this.data.description}" class="centered description">
              <gz-text fontSize="${FontSizes.HEADING_6}">${this.data.description}</gz-text>
            </gz-tooltip>

            <div class="centered">
              <gz-button
                type="${ButtonTypes.QUIET}"
                buttonFontSize="${ButtonFontSizes.SMALL}"
                buttonHeight="${ButtonHeights.SMALL}"
                class="link-button"
                @click="${() => this.handleLinkClick()}"
              >
                ${translateFn('Learn more')}
              </gz-button>
            </div>
          </div>

          <div class="card-logo-container">${this.renderImage()}</div>
        </div>

        <div class="status-line">
          ${this.renderLineLabel(translateFn('Status: '), this.data.status)}
          ${this.data.statusType === LICENSE_STATUSES_INSTALLED.ACTION_REQUIRED
            ? this.renderActionRequiredTooltip()
            : this.renderLineValue(this.data.status)}
        </div>

        ${this.renderDetails()} ${this.renderButtons()}
      </div>
    `;
  }

  private renderActionRequiredTooltip(): TemplateResult {
    if (this.data.type === INTEGRATION_TYPES.INTEGRATION_TYPE_CONTAINER_IMAGE_SCANNER) {
      return html`
        <gz-tooltip .tooltipText="${translateFn('Perform an initial scan')}">
          ${this.renderLineValue(this.data.status)}
        </gz-tooltip>
      `;
    }

    // Default case
    return html`
      <gz-tooltip .tooltipText="${translateFn('You must assign an endpoint as Integrator from Network menu')}">
        ${this.renderLineValue(this.data.status)}
      </gz-tooltip>
    `;
  }

  private renderImage(): TemplateResult {
    if (this.data.image) {
      return html`<img id="card-logo" src="app/webcomponents/assets/images/integrations/${this.data.image}" />`;
    }
    return html`<gz-icon color="transparent" size="72"
      >>
      <svg slot="custom-svg" width="72" height="72" viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="71" height="71" rx="35.5" stroke="var(--grey-layout-201-disabled-text)" />
        <path
          d="M37 41C37 38.7909 38.7909 37 41 37H45C47.2091 37 49 38.7909 49 41V45C49 47.2091 47.2091 49 45 49H41C38.7909 49 37 47.2091 37 45V41Z"
          fill="var(--grey-layout-201-disabled-text)"
        />
        <rect x="23" y="37" width="12" height="12" rx="4" fill="var(--grey-layout-201-disabled-text)" />
        <rect x="23" y="23" width="12" height="12" rx="4" fill="var(--grey-layout-201-disabled-text)" />
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M43 23C41.8954 23 41 23.8954 41 25V27H39C37.8954 27 37 27.8954 37 29C37 30.1046 37.8954 31 39 31H41V33C41 34.1046 41.8954 35 43 35C44.1046 35 45 34.1046 45 33V31H47C48.1046 31 49 30.1046 49 29C49 27.8954 48.1046 27 47 27H45V25C45 23.8954 44.1046 23 43 23Z"
          fill="var(--grey-layout-201-disabled-text)"
        />
      </svg>
    </gz-icon>`;
  }

  private renderDetails(): TemplateResult | undefined {
    if (!this.data.details) {
      return;
    }
    const company = this.data.company.name;
    const integrationName = this.data.details.integrationName;
    // tslint:disable-next-line:prefer-const
    let { lastSync, started, stopped } = this.data.details;
    lastSync = lastSync ? dateFormatter(lastSync, this.dependencies.getTimezone(), 'D MMM YYYY, HH:mm') : 'N/A';
    started = started ? dateFormatter(started, this.dependencies.getTimezone(), 'D MMM YYYY, HH:mm') : 'N/A';
    stopped = stopped ? dateFormatter(stopped, this.dependencies.getTimezone(), 'D MMM YYYY, HH:mm') : 'N/A';

    return html`
      <gz-text fontSize="${FontSizes.PARAGRAPH_11}" fontWeight="${FontWeights.SEMIBOLD}" class="details-title">
        ${translateFn('Details')}
      </gz-text>

      <div class="details-container">
        <div class="details-name-container">
          ${this.renderLineLabel(translateFn('Integration name:'), integrationName)}
          ${Application.Main.company.isPartner() ? this.renderLineLabel(translateFn('Company:'), company) : ''}
          ${this.renderLineLabel(translateFn('Last sync:'), lastSync)}
          ${this.renderLineLabel(translateFn('Running since:'), started)}
          ${this.renderLineLabel(translateFn('Stopped since:'), stopped)}
          ${this.renderLineLabel(translateFn('History logs:'), this.data.historyLogsVisible)}
        </div>
        <div class="details-value-container">
          ${this.renderLineValue(integrationName)}
          ${Application.Main.company.isPartner() ? this.renderLineValue(company) : ''} ${this.renderLineValue(lastSync)}
          ${this.renderLineValue(started)} ${this.renderLineValue(stopped)} ${this.renderHistoryButton()}
        </div>
      </div>
    `;
  }

  private renderButtons(): TemplateResult {
    return html`
      <div class="buttons-container">${this.data.actionButtons?.map(button => this.renderButton(button))}</div>
    `;
  }

  private renderButton(button: IntegrationsMarketplaceCardActionButton): TemplateResult {
    return html`
      <gz-button
        type="${button.type}"
        color="${button.color}"
        id="${button.id}"
        ?disabled="${button.disabled ?? false}"
        class="${button.disabled ? 'disabled' : ''}"
        @click="${e => this.handleAction(e, IntegrationsMarketplaceCardEvents.ACTION_BUTTON_CLICK, button)}"
      >
        ${button.label}
      </gz-button>
    `;
  }

  private renderLineLabel(label: string, value: string | unknown | undefined): TemplateResult | undefined {
    if (value) {
      return html` <gz-text fontSize="${FontSizes.HEADING_6}" class="line-label">${label}</gz-text> `;
    }
  }

  private renderLineValue(value: string | boolean | undefined): TemplateResult | undefined {
    if (value) {
      return html`
        <gz-tooltip isEllipsis .tooltipText="${value}" class="line-value d-block">
          <gz-text fontSize="${FontSizes.HEADING_6}" class="d-inline">${value}</gz-text>
        </gz-tooltip>
      `;
    }
  }

  private renderHistoryButton(): TemplateResult | undefined {
    if (this.data.historyLogsVisible === true) {
      return html`
        <gz-button
          type="${ButtonTypes.QUIET}"
          buttonFontSize="${ButtonFontSizes.SMALL}"
          buttonHeight="${ButtonHeights.SMALL}"
          class="link-button"
          @click="${e => this.handleAction(e, IntegrationsMarketplaceCardEvents.HISTORY_LOGS_CLICK)}"
        >
          ${translateFn('View Details')}
        </gz-button>
      `;
    }
  }

  private handleAction(
    event: Event,
    customEvent: IntegrationsMarketplaceCardEvents,
    button?: IntegrationsMarketplaceCardActionButton
  ): void {
    event.stopPropagation(); // prevent card click event from firing when another element is clicked
    const buttonData = button ? { button } : {};
    this.dispatchEvent(
      new CustomEvent(customEvent, {
        detail: { ...buttonData, data: this.data },
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleLinkClick() {
    window.open(this.data.linkLabel, '_blank');
  }
}
